const {
  __experimentalVStack: VStack,
  __experimentalHStack: HStack,
  __experimentalDropdownContentWrapper: DropdownContentWrapper,
  Button,
  FlexItem,
  Dropdown,
  __experimentalUnitControl: UnitControl,
  __experimentalGrid: Grid,
  __experimentalToggleGroupControl: ToggleGroupControl,
  __experimentalToggleGroupControlOption: ToggleGroupControlOption,
  ColorPalette,
} = wp.components;

const { __ } = wp.i18n;
const { useState, useMemo } = wp.element;
const { useSetting } = wp.blockEditor;
import { parseBoxShadow, unParseBoxShadow } from "./utils";
import "./style.css";

export function shadowPresets() {
  // Get all shadow presets from WordPress
  const defaultShadowPresets = useSetting("shadow.presets.default") || [];
  const themeShadowPresets = useSetting("shadow.presets.theme") || [];
  const shadowPresets = useSetting("shadow.presets.custom") || [];
  // const unsetShadowPreset = useSetting("shadow.presets.unset") || [];
  // Combine all presets (default + theme + custom)
  const allPresets = [
    {
      key: "unset",
      label: __("Unset", "brandy-blocks"),
      shadow: "unset",
      origin: "unset",
    },
    {
      key: "none",
      label: __("None", "brandy-blocks"),
      shadow: "none",
      origin: "none",
    },
    ...defaultShadowPresets.map((preset) => ({
      key: preset.slug,
      label: preset.name,
      shadow: preset.shadow,
      origin: "default",
    })),
    ...themeShadowPresets.map((preset) => ({
      key: preset.slug,
      label: preset.name,
      shadow: preset.shadow,
      origin: "theme",
    })),
    ...shadowPresets.map((preset) => ({
      key: preset.slug,
      label: preset.name,
      shadow: preset.shadow,
      origin: "custom",
    })),
  ];

  return allPresets;
}

function renderShadowToggle({ label }) {
  return ({ onToggle, isOpen }) => {
    const toggleProps = {
      onClick: onToggle,
      className: isOpen ? "is-open" : "",
      "aria-expanded": isOpen,
      style: {
        width: "100%",
      },
    };

    return (
      <Button __next40pxDefaultSize {...toggleProps}>
        <HStack justify="flex-start">
          <div
            style={{
              width: 24,
              height: 24,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              ariaHidden="true"
              focusable="false"
            >
              <path d="M12 8c-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4-1.8-4-4-4zm0 6.5c-1.4 0-2.5-1.1-2.5-2.5s1.1-2.5 2.5-2.5 2.5 1.1 2.5 2.5-1.1 2.5-2.5 2.5zM12.8 3h-1.5v3h1.5V3zm-1.6 18h1.5v-3h-1.5v3zm6.8-9.8v1.5h3v-1.5h-3zm-12 0H3v1.5h3v-1.5zm9.7 5.6 2.1 2.1 1.1-1.1-2.1-2.1-1.1 1.1zM8.3 7.2 6.2 5.1 5.1 6.2l2.1 2.1 1.1-1.1zM5.1 17.8l1.1 1.1 2.1-2.1-1.1-1.1-2.1 2.1zM18.9 6.2l-1.1-1.1-2.1 2.1 1.1 1.1 2.1-2.1z"></path>
            </svg>
          </div>
          <FlexItem>{label ?? __("Drop shadow")}</FlexItem>
        </HStack>
      </Button>
    );
  };
}

function ShadowPopover({ shadow, onChange }) {
  const presets = shadowPresets();
  const isPreset = presets.some((preset) => preset.shadow === shadow);
  const [tabActive, setTabActive] = useState(isPreset ? "presets" : "custom");
  // Get shadow object from shadow string
  const shadowObj = useMemo(() => parseBoxShadow(shadow), [shadow]);

  const onShadowChange = (key, value) => {
    const newShadow = {
      ...((shadowObj && shadowObj[0]) || {
        inset: false,
        color: "#0000001A",
        offsetX: "0px",
        offsetY: "2px",
        blurRadius: "3px",
        spreadRadius: "0px",
      }),
      [key]: value,
    };
    onChange(unParseBoxShadow(newShadow));
  };

  return (
    <VStack spacing={4}>
      <ToggleGroupControl
        __nextHasNoMarginBottom
        value={tabActive}
        isBlock
        onChange={(value) => setTabActive(value)}
        hideLabelFromVision
        __next40pxDefaultSize
      >
        <ToggleGroupControlOption value="presets" label={__("Presets")} />
        <ToggleGroupControlOption value="custom" label={__("Custom")} />
      </ToggleGroupControl>
      {tabActive === "presets" && (
        <div
          className="brandy-editor-box-shadow-presets"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 15,
            justifyContent: "center",
          }}
        >
          {presets.map((preset) => (
            <div
              className={`brandy-editor-box-shadow-presets-item ${
                shadow === preset.shadow ? "selected" : ""
              }`}
              key={preset.key}
              style={{
                boxShadow: `${preset.shadow}`,
              }}
              onClick={() => onChange(preset.shadow)}
            >
              <span style={{ fontSize: 10 }}>{preset.label}</span>
            </div>
          ))}
        </div>
      )}
      {tabActive === "custom" && (
        <>
          <ColorPalette
            clearable={false}
            enableAlpha={true}
            __experimentalIsRenderedInSidebar={true}
            value={
              shadow !== "unset" && shadow !== "" && shadowObj
                ? shadowObj[0]?.color
                : "#0000001A"
            }
            onChange={(value) => onShadowChange("color", value)}
          />
          <ToggleGroupControl
            __nextHasNoMarginBottom
            value={
              shadow !== "unset" && shadow !== "" && shadowObj
                ? shadowObj[0]?.inset
                  ? "inset"
                  : "outset"
                : "outset"
            }
            isBlock
            onChange={(value) => onShadowChange("inset", value === "inset")}
            hideLabelFromVision
            __next40pxDefaultSize
          >
            <ToggleGroupControlOption value="outset" label={__("Outset")} />
            <ToggleGroupControlOption value="inset" label={__("Inset")} />
          </ToggleGroupControl>
          <Grid columns={2} gap={4}>
            <ShadowInputControl
              label={__("X Position")}
              value={
                shadow !== "unset" && shadow !== "" && shadowObj
                  ? shadowObj[0]?.offsetX
                  : "0px"
              }
              onChange={(value) => onShadowChange("offsetX", value)}
            />
            <ShadowInputControl
              label={__("Y Position")}
              value={
                shadow !== "unset" && shadow !== "" && shadowObj
                  ? shadowObj[0]?.offsetY
                  : "0px"
              }
              onChange={(value) => onShadowChange("offsetY", value)}
            />
            <ShadowInputControl
              label={__("Blur")}
              value={
                shadow !== "unset" && shadow !== "" && shadowObj
                  ? shadowObj[0]?.blurRadius
                  : "0px"
              }
              onChange={(value) => onShadowChange("blurRadius", value)}
            />
            <ShadowInputControl
              label={__("Spread")}
              value={
                shadow !== "unset" && shadow !== "" && shadowObj
                  ? shadowObj[0]?.spreadRadius
                  : "0px"
              }
              onChange={(value) => onShadowChange("spreadRadius", value)}
            />
          </Grid>
        </>
      )}
    </VStack>
  );
}

function ShadowInputControl({ label, value, onChange }) {
  const onValueChange = (next) => {
    const isNumeric = next !== undefined && !isNaN(parseFloat(next));
    const nextValue = isNumeric ? next : "0px";
    onChange(nextValue);
  };

  return (
    <UnitControl
      label={label}
      __next40pxDefaultSize
      value={value}
      onChange={onValueChange}
    />
  );
}

export default function ShadowPicker({ shadow, onChange, label }) {
  const popoverProps = {
    placement: "left-start",
    offset: 36,
    shift: true,
  };

  return (
    <Dropdown
      popoverProps={popoverProps}
      className="brandy-shadow-dropdown"
      style={{
        border: "1px solid rgba(0, 0, 0, 0.1)",
        width: "100%",
      }}
      renderToggle={renderShadowToggle({ label })}
      renderContent={() => (
        <DropdownContentWrapper paddingSize="medium" style={{ width: 300 }}>
          <ShadowPopover shadow={shadow} onChange={onChange} />
        </DropdownContentWrapper>
      )}
    />
  );
}
